import React, { useEffect, useState } from 'react';
import { BookOpen, Send, MapPin, Lock, User, MessageSquare, Calendar, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';
import { toast } from 'sonner';

const Guestbook = () => {
  const { language } = useLanguage();
  const content = weddingData[language].guestbook;
  const [isVisible, setIsVisible] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    message: '',
    isPrivate: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Date d'ouverture du livre d'or : 14 août 2026
  const openingDate = new Date('2026-08-14T00:00:00');
  const now = new Date();
  const isOpen = now >= openingDate;

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/guestbook`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error fetching guestbook entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isOpen) {
      toast.error(language === 'fr' 
        ? 'Le livre d\'or sera disponible à partir du 14 août 2026' 
        : 'The guestbook will be available from August 14, 2026');
      return;
    }
    
    if (!formData.name || !formData.city || !formData.message) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill in all fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/guestbook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(content.success);
        setFormData({ name: '', city: '', message: '', isPrivate: false });
        if (!formData.isPrivate) {
          fetchEntries();
        }
      } else {
        toast.error(content.error);
      }
    } catch (error) {
      console.error('Error submitting guestbook entry:', error);
      toast.error(content.error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-16 sm:pt-20 pb-12 sm:pb-20 bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-lilac-100 via-lilac-200 to-lilac-100 py-20 sm:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-white/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-gold-200/20 rounded-full blur-3xl" />
        </div>

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <BookOpen className="mx-auto text-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6" />
          <h1 className="font-playfair font-bold text-plum-900 mb-4 sm:mb-6">
            {content.title}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-plum-700 font-montserrat max-w-2xl mx-auto px-4">
            {content.subtitle}
          </p>
          <div className="divider-gold mt-6 sm:mt-8" />
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {!isOpen ? (
              /* Message quand le livre d'or n'est pas encore ouvert */
              <div className="bg-lilac-50 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center border border-lilac-200 shadow-medium">
                <Clock className="mx-auto text-gold-500 w-16 h-16 sm:w-20 sm:h-20 mb-6" />
                <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-plum-900 mb-4">
                  {language === 'fr' ? 'Bientôt disponible' : 'Coming Soon'}
                </h2>
                <p className="text-base sm:text-lg text-plum-700 font-montserrat mb-6 leading-relaxed">
                  {language === 'fr' 
                    ? 'Le livre d\'or sera ouvert à partir du jour du mariage.' 
                    : 'The guestbook will be open from the wedding day.'}
                </p>
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full border border-gold-200 shadow-soft">
                  <Calendar className="text-gold-500 w-5 h-5" />
                  <span className="text-plum-900 font-montserrat font-semibold">
                    14 août 2026
                  </span>
                </div>
              </div>
            ) : (
              /* Formulaire quand le livre d'or est ouvert */
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-medium border border-lilac-100">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="label-premium flex items-center">
                      <User size={16} className="mr-2 text-gold-500" />
                      {content.form.name}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-premium"
                      required
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="label-premium flex items-center">
                      <MapPin size={16} className="mr-2 text-gold-500" />
                      {content.form.city}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-premium"
                      required
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="label-premium flex items-center">
                      <MessageSquare size={16} className="mr-2 text-gold-500" />
                      {content.form.message}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder={content.form.messagePlaceholder}
                      className="input-premium resize-none"
                      required
                    />
                  </div>

                  {/* Private checkbox */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="isPrivate"
                      id="isPrivate"
                      checked={formData.isPrivate}
                      onChange={handleChange}
                      className="w-5 h-5 text-gold-500 border-2 border-lilac-300 rounded focus:ring-gold-500"
                    />
                    <label htmlFor="isPrivate" className="text-sm font-montserrat text-plum-700 flex items-center">
                      <Lock size={14} className="mr-2 text-gold-500" />
                      {content.form.isPrivate}
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={20} />
                    <span>{submitting ? content.form.submitting : content.form.submit}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Messages Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-white via-lilac-50/50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-playfair font-bold text-plum-900 text-center mb-8 sm:mb-12">
              {content.messagesTitle}
            </h2>

            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-lilac-200 border-t-gold-500 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <BookOpen className="mx-auto text-lilac-300 w-16 h-16 mb-4" />
                <p className="text-lg sm:text-xl font-montserrat text-plum-600 italic">
                  {content.empty}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                {entries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`bg-white rounded-2xl p-5 sm:p-6 shadow-medium border border-lilac-100 hover:shadow-hover hover:-translate-y-1 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-lilac-200 to-lilac-300 rounded-full flex items-center justify-center text-plum-900 font-playfair font-bold text-lg sm:text-xl flex-shrink-0">
                        {entry.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-playfair font-bold text-plum-900 text-base sm:text-lg mb-1">
                          {entry.name}
                        </h3>
                        <div className="flex flex-wrap items-center text-plum-600 text-xs sm:text-sm font-montserrat mb-3 gap-1">
                          <MapPin size={12} className="flex-shrink-0" />
                          <span>{entry.city}</span>
                          <span className="mx-1">•</span>
                          <span>{formatDate(entry.createdAt)}</span>
                        </div>
                        <p className="text-plum-700 font-montserrat text-sm sm:text-base leading-relaxed italic">
                          "{entry.message}"
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Guestbook;
